/**
 * Created: 2026-05-10
 * Initial commit: Add coupon issue page (feat/coupon/mr-6)
 *
 * Page for issuing coupons to users. Shows user search, user coupon history,
 * and an issue button to issue a new coupon from a selected template.
 */

import { gql } from '@apollo/client'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react'
import { currency, message } from '@homemaster/jslib'
import AddIcon from '@mui/icons-material/Add'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    disabled: boolean
    onIssue: () => void
  }
}

import { useState } from 'react'
import UserAvatar from '../components/UserAvatar'
import {
  CouponStatus,
  CouponTemplatesDocument,
  type CouponTemplatesQuery,
  type Gender,
  IssueCouponDocument,
  UserCouponsDocument,
  UsersDocument,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'
import { COUPON_CATEGORY_LABEL, SPORT_LABEL } from '../lib/domainLabels'

// Created: 2026-05-10 | Add coupon issue page queries and mutations
gql`
  query UserCoupons($siteId: UUID!, $userId: UUID!) {
    userCoupons(siteId: $siteId, userId: $userId) {
      id
      status
      issuedAt
      expiresAt
      template {
        id
        name
        category
        sport
        discountAmount
      }
    }
  }

  query Users($userFilter: UserFilterInput!) {
    users(userFilter: $userFilter) {
      id
      openid
      name
      avatar
      mobile
      gender
    }
  }

  mutation IssueCoupon($couponTemplateId: ID!, $userId: ID!) {
    issueCoupon(couponTemplateId: $couponTemplateId, userId: $userId) {
      ... on UserCoupon {
        id
      }
      ... on GraphqlRequestError {
        message
        code
      }
    }
  }
`

type CouponTemplate = NonNullable<CouponTemplatesQuery['couponTemplates']>[number]

type UserResult = {
  id: string
  name: string | null | undefined
  avatar: string | null | undefined
  mobile: string | null | undefined
  gender: Gender | null | undefined
}

const STATUS_LABEL: Record<CouponStatus, string> = {
  [CouponStatus.Active]: '有效',
  [CouponStatus.Used]: '已使用',
  [CouponStatus.Voided]: '已作废',
}

interface CouponGridToolbarProps {
  disabled: boolean
  onIssue: () => void
}

function CouponGridToolbar({ disabled, onIssue }: CouponGridToolbarProps) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 4px 0px' }}>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={onIssue}
      >
        发放
      </Button>
    </Box>
  )
}

const columns: GridColDef[] = [
  {
    field: 'templateName',
    headerName: '模板名称',
    width: 200,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'category',
    headerName: '类型',
    width: 100,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'sport',
    headerName: '运动',
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'status',
    headerName: '状态',
    width: 80,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'issuedAt',
    headerName: '发放时间',
    width: 120,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'expiresAt',
    headerName: '到期日',
    width: 110,
    headerAlign: 'center',
    align: 'center',
  },
]

interface IssueCouponDialogProps {
  user: UserResult
  templates: CouponTemplate[]
  onClose: () => void
  onIssued: () => void
}

function IssueCouponDialog({
  user,
  templates,
  onClose,
  onIssued,
}: IssueCouponDialogProps) {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    templates[0]?.id ?? '',
  )
  const [issuing, setIssuing] = useState(false)
  const [issueCoupon] = useMutation(IssueCouponDocument)

  const handleIssue = async () => {
    if (!selectedTemplateId) return
    setIssuing(true)
    try {
      const { data } = await issueCoupon({
        variables: { couponTemplateId: selectedTemplateId, userId: user.id },
      })
      const result = data?.issueCoupon
      if (result?.__typename === 'GraphqlRequestError') {
        message.error(result.message ?? '发放失败')
        return
      }
      message.success('优惠券已发放', { autoHide: 2000 })
      onIssued()
      onClose()
    } catch (err) {
      message.error(`发放失败: ${err instanceof Error ? err.message : err}`)
    } finally {
      setIssuing(false)
    }
  }

  return (
    <Dialog open onClose={onClose} disableRestoreFocus maxWidth="xs" fullWidth>
      <DialogTitle>发放优惠券 — {user.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <RadioGroup
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            sx={{ gap: 1 }}
          >
            {templates.map((t) => (
              <FormControlLabel
                key={t.id}
                value={t.id}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {COUPON_CATEGORY_LABEL[t.category]}
                      {t.discountAmount != null ? ` ${currency(t.discountAmount)}` : ''}
                      {t.sport ? ` · ${SPORT_LABEL[t.sport] ?? t.sport}` : ''}
                    </Typography>
                  </Box>
                }
                sx={{
                  mx: 0,
                  alignItems: 'center',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: selectedTemplateId === t.id ? 'primary.main' : 'divider',
                  bgcolor: selectedTemplateId === t.id ? 'primary.50' : 'transparent',
                }}
              />
            ))}
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          onClick={handleIssue}
          disabled={!selectedTemplateId || issuing}
        >
          确认发放
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function UserCouponsPage() {
  const { currentSite } = useSiteContext()
  const siteId = currentSite?.id as string

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<UserResult[]>([])
  const [selectedUser, setSelectedUser] = useState<UserResult | null>(null)
  const [issueDialogOpen, setIssueDialogOpen] = useState(false)

  const [findUsers, { loading: lookingUp }] = useLazyQuery(UsersDocument, {
    fetchPolicy: 'network-only',
  })

  const { data: templatesData } = useQuery(CouponTemplatesDocument, {
    variables: { siteId },
    skip: !currentSite,
    fetchPolicy: 'network-only',
  })
  const templates = templatesData?.couponTemplates ?? []

  const {
    data: couponsData,
    loading: couponsLoading,
    refetch: refetchCoupons,
  } = useQuery(UserCouponsDocument, {
    variables: { siteId, userId: selectedUser?.id as string },
    skip: !selectedUser,
    fetchPolicy: 'network-only',
  })
  const userCoupons = couponsData?.userCoupons ?? []

  const canSearch = inputValue.trim().length >= 1

  const handleSearch = async () => {
    if (!canSearch) return
    const { data } = await findUsers({
      variables: { userFilter: { siteId, query: inputValue.trim() } },
    })
    const found = (data?.users ?? []).map((u) => ({
      id: u.id ?? '',
      name: u.name,
      avatar: u.avatar,
      mobile: u.mobile,
      gender: u.gender,
    }))
    setOptions(found)
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>
        用户优惠券
      </Typography>

      <Stack spacing={2} sx={{ maxWidth: 480, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Autocomplete
            sx={{ flex: 1 }}
            options={options}
            getOptionLabel={(u) => `${u.name ?? ''} ${u.mobile ?? ''}`.trim()}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedUser}
            inputValue={inputValue}
            onInputChange={(_, val) => {
              setInputValue(val)
              if (val === '') {
                setOptions([])
                setSelectedUser(null)
              }
            }}
            onChange={(_, val) => {
              setSelectedUser(val)
              setOptions([])
            }}
            filterOptions={(x) => x}
            noOptionsText="未找到该用户"
            renderInput={(params) => (
              <TextField
                {...params}
                label="手机号或姓名"
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSearch) handleSearch()
                }}
              />
            )}
            renderOption={(props, u) => (
              <li {...props} key={u.id}>
                <UserAvatar user={u} />
              </li>
            )}
          />
          <Button
            variant="outlined"
            onClick={handleSearch}
            disabled={!canSearch || lookingUp}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {lookingUp ? <CircularProgress size={18} /> : '查找'}
          </Button>
        </Stack>
      </Stack>

      {selectedUser && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {selectedUser.name} 的优惠券
          </Typography>
          <DataGridPro
            rows={userCoupons.map((c) => ({
              id: c.id,
              templateName: c.template.name,
              category:
                COUPON_CATEGORY_LABEL[c.template.category] ?? c.template.category,
              sport: c.template.sport
                ? (SPORT_LABEL[c.template.sport] ?? c.template.sport)
                : '不限',
              status: STATUS_LABEL[c.status] ?? c.status,
              issuedAt: new Date(c.issuedAt).toLocaleDateString('zh-CN'),
              expiresAt: c.expiresAt,
            }))}
            columns={columns}
            loading={couponsLoading}
            autoHeight
            hideFooter
            showToolbar
            disableRowSelectionOnClick
            slots={{ toolbar: CouponGridToolbar }}
            slotProps={{
              toolbar: {
                disabled: templates.length === 0,
                onIssue: () => setIssueDialogOpen(true),
              },
            }}
          />
        </>
      )}

      {issueDialogOpen && selectedUser && (
        <IssueCouponDialog
          user={selectedUser}
          templates={templates}
          onClose={() => setIssueDialogOpen(false)}
          onIssued={() => refetchCoupons()}
        />
      )}
    </Box>
  )
}
